import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  GET_ALL_JOB_REQUESTS,
  DOCUMENT_UPLOAD,
  DOCUMENT_DELETE,
  S3_SIGNED_URL,
  UPDATE_JOB_REQUESTS,
} from '../../apiConst';
import {
  postRequest,
  putRequest,
  deleteRequest,
} from '../interceptor/interceptor';
import { useForm } from 'react-hook-form';
import FullScreenLoader from '../Loader/FullScreenLoader';
import JobsList from './JobsList';
import { jobRequestFields } from '../../Fields/jobRequestFields';
import UploadField from '../../Common/UploadField';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import CustomModal from '../../Common/CustomModal';

export default function JobRequests() {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(rowsPerPage);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [modal, setModal] = useState(false);
  const [keyValue, setKeyValue] = useState('');
  const [signedUrls, setSignedUrls] = useState([]);
  const [reqId, setReqId] = useState('');
  const navigate = useNavigate();

  const {
    register,
    getValues,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({});

  useEffect(() => {
    fetchListData();
  }, []);

  const fetchListData = (pg = 1, size = 10, search = '', searchKey = null) => {
    setIsLoading(true);

    let payload = {
      filter: {},
      sortOrder: -1,
      page: pg,
      pageSize: size,
    };

    const { searchField } = getValues();
    const key = searchKey ? searchKey : searchField;
    if (key && search) {
      payload.filter[key] = search;
    }
    postRequest(GET_ALL_JOB_REQUESTS, payload)
      .then((res) => {
        console.log('res', res);
        const { result, totalCount } = res.data;
        setTableRowsData(result || []);
        setTotalCount(totalCount || result.length);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setTableRowsData([]);
        setTotalCount(0);
      });
  };

  const getSignedUrlForKey = async (key) => {
    if (!key) return null;
    try {
      const resp = await postRequest(`${S3_SIGNED_URL}`, { keys: [key] });
      const data = resp?.data;

      if (Array.isArray(data)) {
        const found = data.find((it) => it?.key === key);
        return found?.url || found?.signedUrl || found?.presignedUrl || null;
      }

      if (data && typeof data === 'object') {
        if (data[key]) return data[key];
        if (
          data.key === key &&
          (data.url || data.signedUrl || data.presignedUrl)
        )
          return data.url || data.signedUrl || data.presignedUrl;
      }

      return null;
    } catch (err) {
      console.error('getSignedUrlForKey error', err);
      return null;
    }
  };

  const handleDownload = async (jobRequest) => {
    console.log('🚀 ~ handleDownload ~ jobRequest:', jobRequest);
    try {
      setIsLoading(true);

      const key = jobRequest?.attachment?.key;
      if (!key) {
        toast.error('No resume to download');
        setIsLoading(false);
        return;
      }

      const signedUrl = await getSignedUrlForKey(key);
      if (signedUrl) {
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Unable to fetch download link');
      }
    } catch (error) {
      console.error('Download failed', error);
      toast.error('Download failed');
    } finally {
      setIsLoading(false);
    }
  };

  const onUpload = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    setValue('uploadFile', '');
    setValue('uploadFileName', '');

    postRequest(`${DOCUMENT_UPLOAD}`, formData, {
      'Content-Type': 'multipart/form-data',
    })
      .then((res) => {
        console.log('fileurl:--->', res);
        setValue('uploadFile', res.data.data[0].url);
        setValue('uploadFileName', res.data.data[0].key);
        setKeyValue(res.data.data[0].key);
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const onRemoveFile = async (e, keyValue) => {
    console.log('🚀 ~ onRemoveFile ~ keyValue:', keyValue);
    console.log('removed cliked', e);
    const res = await deleteRequest(`${DOCUMENT_DELETE}${keyValue}`);
    try {
      console.log('🚀 ~ onRemoveFile ~ res:', res);
      if (res) {
        toast.success(res?.data?.message);
        setValue('uploadFileName', '');
        setValue('uploadFile', '');
        reset({ uploadFile: '', uploadFileName: '' });
        fetchListData();
      }
    } catch (error) {
      console.log('🚀 ~ onRemoveFile ~ error:', error);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      attachment: { url: data?.uploadFile, key: data?.uploadFileName },
    };
    if (!data?.uploadFileName || !data?.uploadFile) {
      toast.error('Please upload a file before submitting.');
      return;
    }
    const res = await putRequest(`${UPDATE_JOB_REQUESTS}/${reqId}`, payload);
    try {
      if (res) {
        toast.success(res?.data?.message);
        setModal(false);
        setReqId('');
        reset({ uploadFile: '', uploadFileName: '' });
        fetchListData();
      }
    } catch (error) {
      console.log('🚀 ~ onSubmit ~ error:', error);
    }
  };

  const openModal = (data) => {
    setModal(!modal);
    setReqId(data?._id);
  };

  const pressCloseButton = () => {
    setModal(!modal);
    setReqId('');
  };

  const ModalBody = () => {
    return (
      <>
        <form className="w-full flex justify-center items-center flex-col p-5">
          <div className="w-full grid md:grid-cols-1 gap-6">
            {jobRequestFields.map((field) => {
              const {
                fieldTitle,
                fieldName,
                fieldType,
                validationObj,
                disabled,
                options,
              } = field;
              return (
                <div className="flex flex-col w-full" key={fieldName}>
                  <label className="block text-gray-800 font-bold mb-3 pr-4">
                    {fieldTitle}
                  </label>
                  <div className="w-full flex items-center">
                    <div className="w-full">
                      {fieldType === 'upload' && (
                        <UploadField
                          fieldName={fieldName}
                          validationObj={validationObj}
                          register={register}
                          errors={errors}
                          fieldType={fieldType}
                          onChange={onUpload}
                          onRemoveFile={onRemoveFile}
                          valueClassName="flex m-2 items-center"
                          value={getValues('uploadFileName')}
                          placeholder={fieldTitle}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex justify-center">
              <Button
                type="button"
                variant="contained"
                className="mt-4 px-6 py-2 rounded-md bg-success-700 text-white transition duration-150 hover:bg-success-600"
                onClick={handleSubmit(onSubmit)}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      </>
    );
  };

  // const onView = (task) => {
  //   navigate('/view/jobs-list', {
  //     state: {
  //       data: {
  //         task: {
  //           ...task,
  //         },
  //       },
  //       isView: true,
  //     },
  //   });
  // };
  return (
    <>
      {isLoading && <FullScreenLoader />}
      <CustomModal
        modal={modal}
        body={<ModalBody />}
        heading="Upload Resume"
        pressCloseButton={pressCloseButton}
      />
      <JobsList
        tableRowsData={tableRowsData}
        getListData={fetchListData}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        openModal={openModal}
        register={register}
        errors={errors}
        onDownload={handleDownload}
      />
    </>
  );
}
